import React from 'react';
import { Show, useRecordContext, useTranslate } from 'react-admin';
import { Box, Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { MarkRefundedByHandButton } from './MarkRefundedByHandButton';
import { MarkDoubleRefundHandledButton } from './MarkDoubleRefundHandledButton';
import { ProviderActions } from './ProviderActions';
import { RefundStateChip, SettleStateChip } from './KioskTransactionsList';
import { isSoepayMethod, paidWith, valueLabel } from './refundState';
import type { KioskTransactionDetail, ReceiptLine } from './types';

const money = (value: number | null) => (value === null ? '-' : `HK$${value.toFixed(2)}`);
const when = (iso: string | null) => (iso === null ? '-' : new Date(iso).toLocaleString());

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, borderBottom: '1px solid #F0F0F0' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" component="div" sx={{ textAlign: 'right' }}>{value}</Typography>
  </Box>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Card sx={{ flex: 1, minWidth: 0 }}>
    <CardContent>
      <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
      {children}
    </CardContent>
  </Card>
);

export function receiptLineLabel(line: ReceiptLine, translate: (key: string, options?: any) => string): string {
  switch (line.kind) {
    case 'energy':
      return translate('resources.kiosk-transactions.lines.energy', { kwh: line.energyKwh.toFixed(2), rate: line.rate.toFixed(2), window: line.window === null ? '' : ` (${line.window})` });
    case 'session_fee':
      return translate('resources.kiosk-transactions.lines.sessionFee');
    case 'time_fee':
      return translate('resources.kiosk-transactions.lines.timeFee');
    case 'idle_fee':
      return translate('resources.kiosk-transactions.lines.idleFee');
    case 'deposit_cap':
      return translate('resources.kiosk-transactions.lines.depositCap');
  }
}

const SoepaySection = ({ record, t }: { record: KioskTransactionDetail; t: (key: string, options?: any) => string }) => {
  const translate = useTranslate();
  const instruction = record.settleInstruction;
  const paid = paidWith(record, translate);
  return (
    <Section title={t('sections.soepay')}>
      <Row label={t('fields.paidWith')} value={paid === null ? '-' : paid} />
      <Row label={t('fields.providerRef')} value={record.providerRef === null ? '-' : record.providerRef} />
      <Row label={t('fields.settleState')} value={<SettleStateChip state={record.settleState} />} />
      <Row label={t('fields.settleType')} value={record.settleType === null ? '-' : valueLabel(translate, 'settleTypes', record.settleType)} />
      {instruction !== null && (
        <>
          <Row label={t('fields.instructionStatus')} value={valueLabel(translate, 'instructionStatuses', instruction.status)} />
          <Row label={t('fields.instructionAmount')} value={money(instruction.amount)} />
          <Row label={t('fields.instructionAttempts')} value={instruction.attempts} />
          <Row label={t('fields.sentCall')} value={instruction.sentCall === null ? '-' : valueLabel(translate, 'sentCalls', instruction.sentCall)} />
          <Row label={t('fields.failureReason')} value={instruction.failureReason === null ? '-' : valueLabel(translate, 'failureReasons', instruction.failureReason)} />
          <Row label={t('fields.checks')} value={instruction.checks} />
          <Row label={t('fields.lastCheck')} value={instruction.lastCheck === null ? '-' : valueLabel(translate, 'lastChecks', instruction.lastCheck)} />
          <Row label={t('fields.lastCheckedAt')} value={when(instruction.lastCheckedAt)} />
          <Row label={t('fields.nextCheckAt')} value={when(instruction.nextCheckAt)} />
          <Row label={t('fields.lastError')} value={instruction.lastError === null ? '-' : instruction.lastError} />
        </>
      )}
    </Section>
  );
};

export const KioskTransactionDetailView = () => {
  const record = useRecordContext<KioskTransactionDetail>();
  const translate = useTranslate();
  if (!record) return null;
  const t = (key: string, options?: any) => translate(`resources.kiosk-transactions.${key}`, options);
  const soepay = isSoepayMethod(record.method);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">{record.receiptReference}</Typography>
          <RefundStateChip state={record.refundState} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <ProviderActions record={record} />
          <MarkRefundedByHandButton record={record} />
          <MarkDoubleRefundHandledButton record={record} />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        <Section title={t('sections.visit')}>
          <Row label={t('fields.createdAt')} value={when(record.createdAt)} />
          <Row label={t('fields.visitStatus')} value={record.visitStatus} />
          <Row label={t('fields.siteName')} value={record.siteName} />
          <Row label={t('fields.kioskName')} value={record.kioskName} />
          <Row label={t('fields.bayNumber')} value={record.bayNumber === null ? `${record.chargerId} #${record.connectorId}` : record.bayNumber} />
          <Row label={t('fields.method')} value={t(`methods.${record.method}`)} />
        </Section>
        <Section title={t('sections.amounts')}>
          <Row label={t('fields.depositAmount')} value={money(record.depositAmount)} />
          <Row label={t('fields.finalCharge')} value={money(record.finalCharge)} />
          <Row label={t('fields.refundAmount')} value={money(record.refundAmount)} />
          <Row label={t('fields.status')} value={record.status} />
        </Section>
        <Section title={t('sections.refund')}>
          <Row label={t('fields.refundState')} value={<RefundStateChip state={record.refundState} />} />
          <Row label={t('fields.refundDueAt')} value={when(record.refundDueAt)} />
          <Row label={t('fields.refundExpiresAt')} value={when(record.refundExpiresAt)} />
          <Row label={t('fields.refundedAt')} value={when(record.refundedAt)} />
          <Row label={t('fields.csRefundedAt')} value={when(record.csRefundedAt)} />
          <Row label={t('fields.csRefundNotes')} value={record.csRefundNotes === null ? '-' : record.csRefundNotes} />
          {record.doubleRefund.refundedTwice && (
            <Row
              label={t('fields.doubleRefund')}
              value={record.doubleRefund.handledAt === null
                ? t('doubleRefund.notHandled')
                : t('doubleRefund.handledLine', { at: when(record.doubleRefund.handledAt), by: record.doubleRefund.handledBy, note: record.doubleRefund.handledNote })}
            />
          )}
        </Section>
      </Box>
      {soepay && <SoepaySection record={record} t={t} />}
      <Section title={t('sections.costBreakdown')}>
        {record.costBreakdownError !== null && <Typography color="error" variant="body2">{t('messages.breakdownUnreconciled', { error: record.costBreakdownError })}</Typography>}
        {record.costBreakdown === null && record.costBreakdownError === null && <Typography variant="body2" color="text.secondary">{t('messages.noBreakdown')}</Typography>}
        {record.costBreakdown !== null && (
          <>
            {record.costBreakdown.lines.map((line, index) => <Row key={index} label={receiptLineLabel(line, translate)} value={money(line.amount)} />)}
            <Row label={t('lines.total')} value={money(record.costBreakdown.total)} />
          </>
        )}
      </Section>
      <Section title={t(soepay ? 'sections.soepayAttempts' : 'sections.attempts')}>
        {record.refundAttempts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t('messages.noAttempts')}</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('attempts.at')}</TableCell>
                  <TableCell>{t('attempts.change')}</TableCell>
                  <TableCell>{t(soepay ? 'attempts.reasonSoepay' : 'attempts.reason')}</TableCell>
                  <TableCell>{t('attempts.count')}</TableCell>
                  <TableCell>{t('attempts.detail')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {record.refundAttempts.map((attempt, index) => (
                  <TableRow key={index}>
                    <TableCell>{when(attempt.at)}</TableCell>
                    <TableCell>{attempt.fromStatus === null ? attempt.toStatus : `${attempt.fromStatus} → ${attempt.toStatus}`}</TableCell>
                    <TableCell>{attempt.failureReason === null ? '-' : valueLabel(translate, 'failureReasons', attempt.failureReason)}</TableCell>
                    <TableCell>{attempt.attempts}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{attempt.outcome !== null ? JSON.stringify(attempt.outcome) : attempt.detail === null ? '-' : attempt.detail}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Section>
    </Box>
  );
};

export const KioskTransactionsShow = () => (
  <Show actions={false}>
    <KioskTransactionDetailView />
  </Show>
);

export default KioskTransactionsShow;
